import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { MDBBtn, MDBContainer, MDBIcon } from 'mdb-react-ui-kit';
import { submitSupportRequest } from '../../api/support';
import { BRANDING } from '../../config/branding';
import { SOCIAL_LINKS } from '../../utils/constants';
import {
  ContentSection,
  FormContainer,
  FormLabel,
  FormTextarea,
  FormInput,
  PageContainer,
  PageSubtitle,
  PageTitle,
  SectionTitle,
  SocialCard,
  SocialDescription,
  SocialIcon,
  SocialInfo,
  SocialMediaContainer,
  SocialTitle,
  SubmitRow,
  StatusMessage
} from './styles';

const PLACEHOLDER_SOCIAL_LINKS = {
  twitter: SOCIAL_LINKS.x,
  instagram: SOCIAL_LINKS.instagram,
  winible: SOCIAL_LINKS.winible
};

const Contact = () => {
  const [fromEmail, setFromEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const onSubmit = async event => {
    event.preventDefault();

    setStatus({ type: '', message: '' });
    setLoading(true);

    try {
      await submitSupportRequest({ fromEmail, message });
      setStatus({ type: 'success', message: 'Support request sent successfully. We will get back to you soon.' });
      setFromEmail('');
      setMessage('');
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to send support request. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{BRANDING.appName} | Contact</title>
        <meta name="description" content={`Get help and support for ${BRANDING.appName}. Contact us through social media or submit a support request.`} />
      </Helmet>
      <MDBContainer>
        <PageTitle>Get In Touch With Us</PageTitle>
        <PageSubtitle>
          We&apos;re here to support you. Reach out through social media or submit a support request below.
        </PageSubtitle>

        <ContentSection>
          <SectionTitle>Connect With Us</SectionTitle>
          <SocialMediaContainer>
            <SocialCard href={PLACEHOLDER_SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" className="twitter">
              <SocialIcon fab icon="twitter" />
              <SocialInfo>
                <SocialTitle>Twitter</SocialTitle>
                <SocialDescription>Follow us for updates and quick support</SocialDescription>
              </SocialInfo>
            </SocialCard>

            <SocialCard href={PLACEHOLDER_SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="instagram">
              <SocialIcon fab icon="instagram" />
              <SocialInfo>
                <SocialTitle>Instagram</SocialTitle>
                <SocialDescription>Stay connected with our community</SocialDescription>
              </SocialInfo>
            </SocialCard>

            <SocialCard href={PLACEHOLDER_SOCIAL_LINKS.winible} target="_blank" rel="noopener noreferrer" className="winible">
              <SocialIcon fas icon="chart-line" />
              <SocialInfo>
                <SocialTitle>Winible</SocialTitle>
                <SocialDescription>See our picks and follow our Winible profile</SocialDescription>
              </SocialInfo>
            </SocialCard>
          </SocialMediaContainer>
        </ContentSection>

        <ContentSection>
          <SectionTitle>Submit a Support Request</SectionTitle>
          <FormContainer>
            {status.message ? <StatusMessage $error={status.type === 'error'}>{status.message}</StatusMessage> : null}
            <form onSubmit={onSubmit}>
              <FormLabel htmlFor="fromEmail">Your Email</FormLabel>
              <FormInput
                id="fromEmail"
                type="email"
                required
                value={fromEmail}
                onChange={event => setFromEmail(event.target.value)}
                placeholder="you@example.com"
              />

              <FormLabel htmlFor="message">Message</FormLabel>
              <FormTextarea
                id="message"
                required
                rows={6}
                value={message}
                onChange={event => setMessage(event.target.value)}
                placeholder="Please describe your issue or question..."
              />
              <SubmitRow>
                <MDBBtn type="submit" disabled={loading} style={{ width: '100%', padding: '0.75rem' }}>
                  {loading ? (
                    <>
                      <MDBIcon fas icon="spinner" spin style={{ marginRight: '0.5rem' }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MDBIcon fas icon="paper-plane" style={{ marginRight: '0.5rem' }} />
                      Send Support Request
                    </>
                  )}
                </MDBBtn>
              </SubmitRow>
            </form>
          </FormContainer>
        </ContentSection>
      </MDBContainer>
    </PageContainer>

  );
};

export default Contact;
